// Shared Supabase utilities for Revvoo
// Extracted from App.js for code splitting

// Supabase Configuration
export const SUPABASE_URL = 'https://stqujupcqdcrqcnrysoz.supabase.co';
export const SUPABASE_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InN0cXVqdXBjcWRjcnFjbnJ5c296Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3Njc1NDU1NDYsImV4cCI6MjA4MzEyMTU0Nn0.fSt8C7M16k1y36Bv--777VkVpUuTynaLXKHJvLH7tdo';

// C3 FIX: Helper to get the logged-in user's access_token from localStorage.
// When present, this token is used in the Authorization header so Supabase RLS
// policies that check auth.uid() actually match the real user.
// Falls back to the anon key for unauthenticated / public queries.
export const getAuthToken = () => {
  try {
    const sessionStr = localStorage.getItem('revvoo_session');
    if (sessionStr) {
      const session = JSON.parse(sessionStr);
      // The session object stores access_token directly (from Supabase Auth response)
      if (session?.access_token) return session.access_token;
      // Some paths store it nested under session.session
      if (session?.session?.access_token) return session.session.access_token;
    }
  } catch (e) {
    // Corrupted session — fall back to anon key
  }
  return SUPABASE_KEY;
};

// Supabase fetch helper
export const supabaseFetch = async (table, method = 'GET', body = null, query = '', { retries = 1, retryDelay = 2000 } = {}) => {
  const authToken = getAuthToken();
  const headers = {
    'apikey': SUPABASE_KEY,
    'Authorization': `Bearer ${authToken}`,
    'Content-Type': 'application/json',
    'Prefer': (method === 'POST' || method === 'PATCH') ? 'return=representation' : undefined
  };

  const options = {
    method,
    headers: Object.fromEntries(Object.entries(headers).filter(([_, v]) => v !== undefined))
  };

  if (body) {
    options.body = JSON.stringify(body);
  }

  let lastError = null;
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const response = await fetch(`${SUPABASE_URL}/rest/v1/${table}${query}`, options);

      if (!response.ok) {
        // Get detailed error from Supabase
        let errorDetails = response.statusText;
        try {
          const errorBody = await response.json();
          errorDetails = errorBody.message || errorBody.error || JSON.stringify(errorBody);
          console.error('Supabase error details:', errorBody);
        } catch (e) {
          // Could not parse error body
        }
        throw new Error(`Supabase error: ${errorDetails}`);
      }

      // Handle empty responses (like successful PATCH with no return)
      const text = await response.text();
      if (!text) return null;

      try {
        return JSON.parse(text);
      } catch (e) {
        return null;
      }
    } catch (error) {
      lastError = error;
      const isNetworkError = error.name === 'TypeError' || error.message === 'Failed to fetch' || error.message.includes('NetworkError');
      // Only retry on network errors, not on data/auth errors
      if (isNetworkError && attempt < retries) {
        console.warn(`Supabase fetch retry ${attempt + 1}/${retries} for ${table}...`);
        await new Promise(resolve => setTimeout(resolve, retryDelay));
        continue;
      }
      // Provide user-friendly message for network errors
      if (isNetworkError) {
        throw new Error('Unable to connect. Please check your internet and try again.');
      }
      throw error;
    }
  }
  throw lastError;
};

// Enhanced Stripe error detail — returns { title, message, canRetry }
export const getStripeErrorDetail = (error) => {
  const msg = error?.message || error?.toString() || '';
  const code = error?.code || error?.decline_code || '';
  const lowerMsg = msg.toLowerCase();

  if (code === 'card_declined' || code === 'generic_decline' || lowerMsg.includes('declined')) {
    return { title: 'Card Declined', message: 'Your card was declined by your bank. Please try a different card.', canRetry: true };
  }
  if (code === 'insufficient_funds' || lowerMsg.includes('insufficient')) {
    return { title: 'Insufficient Funds', message: 'Your card does not have enough funds for this charge. Please try a different card.', canRetry: true };
  }
  // C4 FIX: Check for auth/token errors BEFORE card errors so "expired token" isn't
  // misinterpreted as "expired card". The word "expired" appears in both contexts.
  if (lowerMsg.includes('expired token') || lowerMsg.includes('invalid token') || lowerMsg.includes('authorization required') || lowerMsg.includes('invalid or expired')) {
    return { title: 'Session Expired', message: 'Your session has expired. Please log out and log back in, then try again.', canRetry: false };
  }
  if (code === 'expired_card' || lowerMsg.includes('expired card')) {
    return { title: 'Card Expired', message: 'This card has expired. Please use a different card.', canRetry: true };
  }
  if (code === 'incorrect_cvc' || lowerMsg.includes('cvc') || lowerMsg.includes('security code')) {
    return { title: 'Incorrect Security Code', message: 'The security code (CVC) is incorrect. Please check and try again.', canRetry: true };
  }
  if (code === 'incorrect_number' || lowerMsg.includes('card number')) {
    return { title: 'Invalid Card Number', message: 'The card number is incorrect. Please check and try again.', canRetry: true };
  }
  if (code === 'processing_error' || lowerMsg.includes('processing')) {
    return { title: 'Processing Error', message: 'An error occurred while processing your card. Please try again.', canRetry: true };
  }
  if (code === 'do_not_honor' || code === 'transaction_not_allowed') {
    return { title: 'Transaction Not Allowed', message: 'Your bank declined this transaction. Please contact your bank or try a different card.', canRetry: true };
  }
  if (code === 'lost_card' || code === 'stolen_card') {
    return { title: 'Card Cannot Be Used', message: 'This card cannot be used for payments. Please try a different card.', canRetry: true };
  }
  if (msg === 'Failed to fetch' || msg.includes('NetworkError') || lowerMsg.includes('network')) {
    return { title: 'Connection Error', message: 'Unable to connect to the payment system. Please check your internet and try again.', canRetry: true };
  }
  if (msg.includes('Failed to create payment intent')) {
    return { title: 'Payment Unavailable', message: 'Payment processing is temporarily unavailable. Please try again in a few minutes.', canRetry: true };
  }
  return { title: 'Payment Failed', message: msg || 'Something went wrong with your payment. Please try again.', canRetry: true };
};

// Helper to get user-friendly Stripe error messages (returns string for backward compat)
export const getStripeErrorMessage = (error) => {
  return getStripeErrorDetail(error).message;
};

// Upload file to Supabase Storage
export const uploadToStorage = async (bucket, filePath, file) => {
  const authToken = getAuthToken();
  const response = await fetch(`${SUPABASE_URL}/storage/v1/object/${bucket}/${filePath}`, {
    method: 'POST',
    headers: {
      'apikey': SUPABASE_KEY,
      'Authorization': `Bearer ${authToken}`,
      'Content-Type': file.type,
      'x-upsert': 'true'
    },
    body: file
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Storage upload failed: ${errorText}`);
  }

  // Return public URL
  return `${SUPABASE_URL}/storage/v1/object/public/${bucket}/${filePath}`;
};

// Send SMS notification (disabled)
export const sendSMS = async (phoneNumber, message) => {
  // SMS is disabled
  return { success: true, disabled: true };
};

// Send email notification via Resend
export const sendEmail = async (type, to, data) => {
  try {
    const subjectMap = {
      'new_job': 'New Job Request - Revvoo',
      'job_accepted': 'Your mechanic is on the way! - Revvoo',
      'quote_ready': 'Your Repair Quote is Ready - Revvoo',
      'quote_approved': 'Quote Approved - Begin Repair',
      'job_complete': 'Service Complete - Revvoo Receipt',
      'mechanic_approved': "You're approved! Welcome to Revvoo",
      'mechanic_rejected': 'Revvoo Application Update',
      'job_declined': 'Service Update - Revvoo',
      'quote_declined': 'Quote Declined - Revvoo',
      'booking_cancelled': 'Booking Cancelled - Revvoo'
    };

    const authToken = getAuthToken();
    const response = await fetch(`${SUPABASE_URL}/functions/v1/send-email`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`,
      },
      body: JSON.stringify({
        to,
        subject: subjectMap[type] || 'Revvoo Notification',
        type,
        data
      }),
    });

    const result = await response.json();

    if (!response.ok) {
      console.error('Email send failed:', result);
      return { success: false, error: result.error };
    }

    return { success: true, id: result.id };
  } catch (error) {
    console.error('Email send error:', error);
    return { success: false, error: error.message };
  }
};

// Build mechanic notification SMS (legacy - kept for backwards compatibility)
// eslint-disable-next-line no-unused-vars
export const buildMechanicSMS = (customerName, carProblem, carInfo, bookingId, totalCost) => {
  const acceptLink = `${window.location.origin}/accept-booking/${bookingId}`;

  return `NEW BOOKING REQUEST - Revvoo

Customer: ${customerName}
Vehicle: ${carInfo}
Problem: ${carProblem}

Total: $${totalCost.toFixed(2)}

Accept this job: ${acceptLink}

Reply ACCEPT to confirm or DECLINE to pass.`;
};

// Build mechanic notification SMS for service call fee model
export const buildMechanicSMSServiceCall = (customerName, carProblem, carInfo, bookingId, serviceCallFee, estimatedRepair) => {
  const acceptLink = `${window.location.origin}/accept-booking/${bookingId}`;
  const estimateDisplay = estimatedRepair?.display || '$100 - $200';

  return `NEW SERVICE CALL - Revvoo

Customer: ${customerName}
Vehicle: ${carInfo}
Problem: ${carProblem}

Service Call Fee: $${serviceCallFee.toFixed(2)} (PAID)
Est. Repair: ${estimateDisplay}

Customer has paid service call fee. Accept to inspect vehicle and provide final repair quote.

Accept: ${acceptLink}

Reply ACCEPT or DECLINE`;
};

// ========================================
// SHARED CONSTANTS
// ========================================

export const SERVICE_ZONES = [
  { name: 'North County Coastal', areas: 'Oceanside, Carlsbad, Encinitas, Del Mar' },
  { name: 'North County Inland', areas: 'Escondido, San Marcos, Vista, Poway, Rancho Bernardo' },
  { name: 'Coastal San Diego', areas: 'La Jolla, Pacific Beach, Ocean Beach, Point Loma' },
  { name: 'Central / Midtown', areas: 'Downtown, Hillcrest, North Park, Mission Hills' },
  { name: 'Mid-City', areas: 'Clairemont, Kearny Mesa, Mission Valley, Linda Vista' },
  { name: 'North San Diego', areas: 'Mira Mesa, Scripps Ranch, Rancho Pe\u00f1asquero, Carmel Valley' },
  { name: 'East County', areas: 'El Cajon, La Mesa, Santee, Lakeside, Spring Valley' },
  { name: 'South Bay', areas: 'Chula Vista, National City, Imperial Beach' },
  { name: 'Coronado', areas: 'Coronado Island' }
];

// ========================================
// ZIP CODE TO SERVICE ZONE MAPPING
// ========================================

export const ZIP_TO_ZONE = {
  // North County Coastal
  '92054': 'North County Coastal', '92056': 'North County Coastal', '92057': 'North County Coastal', '92058': 'North County Coastal', // Oceanside
  '92055': 'North County Coastal', '92049': 'North County Coastal', // Oceanside (military/PO Box)
  '92008': 'North County Coastal', '92009': 'North County Coastal', '92010': 'North County Coastal', '92011': 'North County Coastal', // Carlsbad
  '92024': 'North County Coastal', // Encinitas
  '92014': 'North County Coastal', // Del Mar
  '92075': 'North County Coastal', // Solana Beach
  '92007': 'North County Coastal', // Cardiff

  // North County Inland
  '92025': 'North County Inland', '92026': 'North County Inland', '92027': 'North County Inland', '92029': 'North County Inland', '92030': 'North County Inland', // Escondido
  '92069': 'North County Inland', '92078': 'North County Inland', '92096': 'North County Inland', // San Marcos
  '92081': 'North County Inland', '92083': 'North County Inland', '92084': 'North County Inland', '92085': 'North County Inland', // Vista
  '92064': 'North County Inland', // Poway
  '92128': 'North County Inland', // Rancho Bernardo
  '92065': 'North County Inland', // Ramona
  '92082': 'North County Inland', // Valley Center

  // Coastal San Diego
  '92037': 'Coastal San Diego', '92038': 'Coastal San Diego', // La Jolla
  '92109': 'Coastal San Diego', // Pacific Beach
  '92107': 'Coastal San Diego', // Ocean Beach
  '92106': 'Coastal San Diego', // Point Loma
  '92110': 'Coastal San Diego', // Old Town / Morena

  // Central / Midtown
  '92101': 'Central / Midtown', '92102': 'Central / Midtown', // Downtown
  '92103': 'Central / Midtown', // Hillcrest
  '92104': 'Central / Midtown', // North Park
  '92116': 'Central / Midtown', // Normal Heights / University Heights
  '92108': 'Central / Midtown', // Mission Hills / Mission Valley

  // Mid-City
  '92117': 'Mid-City', // Clairemont
  '92111': 'Mid-City', // Kearny Mesa / Linda Vista
  '92123': 'Mid-City', // Kearny Mesa / Serra Mesa
  '92120': 'Mid-City', // Del Cerro / Allied Gardens

  // North San Diego
  '92126': 'North San Diego', // Mira Mesa
  '92131': 'North San Diego', // Scripps Ranch
  '92129': 'North San Diego', // Rancho Peñasquitos
  '92130': 'North San Diego', // Carmel Valley
  '92127': 'North San Diego', // Rancho Bernardo (east)
  '92122': 'North San Diego', // University City
  '92121': 'North San Diego', // Sorrento Valley

  // East County
  '92019': 'East County', '92020': 'East County', '92021': 'East County', // El Cajon
  '91941': 'East County', '91942': 'East County', // La Mesa
  '92071': 'East County', // Santee
  '92040': 'East County', // Lakeside
  '91977': 'East County', '91978': 'East County', // Spring Valley / Casa de Oro
  '91945': 'East County', // Lemon Grove
  '92114': 'East County', // Encanto
  '92115': 'East County', // College Area
  '92105': 'East County', // City Heights
  '92119': 'East County', // San Carlos

  // South Bay
  '91910': 'South Bay', '91911': 'South Bay', '91913': 'South Bay', '91914': 'South Bay', '91915': 'South Bay', // Chula Vista
  '91902': 'South Bay', // Bonita
  '91950': 'South Bay', // National City
  '91932': 'South Bay', // Imperial Beach
  '92113': 'South Bay', // Barrio Logan / Logan Heights
  '92154': 'South Bay', // Otay Mesa
  '92173': 'South Bay', // San Ysidro

  // Coronado
  '92118': 'Coronado', // Coronado
  '92135': 'Coronado'  // Naval Air Station
};

// Look up service zone from ZIP code
export const getZoneFromZip = (zip) => ZIP_TO_ZONE[zip] || null;

// Extract 5-digit ZIP code from a formatted address string
export const extractZipFromAddress = (address) => {
  const match = address.match(/\b(\d{5})\b/);
  return match ? match[1] : null;
};
